import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import './NewDashboard.css';
import './AdminDashboard.css';
import usuarioService from '../services/UsuarioService';
import ColegioService from '../services/ColegioService';
import ActiveColegioBadge from './common/ActiveColegioBadge';
import UserSessionMenu from './common/UserSessionMenu';
import AdminMenu from './common/menus/AdminMenu';
import SearchableSelect from './common/SearchableSelect';
import { useAuth } from '../contexts/AuthContext';
import { ADMIN_FORM_MENU_ITEMS, navigateAdminMenuItem } from '../utils/adminMenu';

const AdminEditProfesor = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { profesorId } = useParams();
  const { user, logout } = useAuth();
  const profesorState = useMemo(() => location.state?.profesor || {}, [location.state]);
  const stateColegioId = location.state?.idColegio || null;
  // Pantalla desde la que se llegó (por ejemplo, la edición de un curso); si no hay, se vuelve al dashboard.
  const returnTo = location.state?.returnTo || null;

  const activeColegio = useMemo(() => ColegioService.getActiveColegio(), []);
  const activeColegioId = useMemo(() => stateColegioId || ColegioService.getActiveColegioId(), [stateColegioId]);
  const activeColegioName = activeColegio?.nombre || activeColegio?.name || activeColegio?.descripcion || 'Colegio';
  const hasModuloAlumnos = useMemo(() => {
    const flags = ColegioService.getFeatureFlags();
    return Array.isArray(flags) && flags.some((flag) => {
      const name = flag?.nombre || flag?.Nombre || '';
      const activo = flag?.activo ?? flag?.Activo ?? false;
      return name.toLowerCase() === 'moduloalumnos' && !!activo;
    });
  }, []);

  const [form, setForm] = useState({
    email: profesorState.email || profesorState.Email || '',
    nombre: profesorState.nombre || profesorState.Nombre || '',
    apellido: profesorState.apellido || profesorState.Apellido || '',
    telefono: profesorState.telefono || profesorState.Telefono || '',
    dni: profesorState.dni || profesorState.Dni || '',
    legajo: profesorState.legajo || profesorState.Legajo || '',
    permiteCrearAsistente: !!(profesorState.permiteCrearAsistente || profesorState.PermiteCrearAsistente || profesorState.PertmiteCrearAsistente),
    permiteCrearAula: !!(profesorState.permiteCrearAula || profesorState.PermiteCrearAula),
  });
  const [cursoMateriaOptions, setCursoMateriaOptions] = useState([]);
  const [cursoMateriasAsignadas, setCursoMateriasAsignadas] = useState([]);
  const [cursoMateriasLoading, setCursoMateriasLoading] = useState(false);
  const [cursoMateriasError, setCursoMateriasError] = useState('');
  const [selectedCursoMateriaId, setSelectedCursoMateriaId] = useState('');
  const [isAssociatingCursoMateria, setIsAssociatingCursoMateria] = useState(false);
  const [cursoMateriasMessage, setCursoMateriasMessage] = useState('');
  const [cursoMateriasMessageType, setCursoMateriasMessageType] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState(''); // success | error | info

  const normalizeList = (list) => (Array.isArray(list) ? list.filter(Boolean) : []);
  const getCursoMateriaId = (item) => String(item?.idCursoMateria || item?.IdCursoMateria || '');
  const getMateriaNombre = (item) => item?.nombreMateria || item?.NombreMateria || 'Materia';
  const getMateriaCodigo = (item) => item?.codigoMateria || item?.CodigoMateria || '';
  const getCursoNombre = (item) => item?.nombreCurso || item?.NombreCurso || 'Curso';
  const getAreaNombre = (item) => item?.nombreArea || item?.NombreArea || '';
  const getCicloLectivo = (item) => item?.cicloLectivo || item?.CicloLectivo || '';
  const currentCicloLectivo = useMemo(() => new Date().getFullYear(), []);

  const cursoMateriaSelectOptions = useMemo(() => {
    const assignedIds = new Set(cursoMateriasAsignadas.map(getCursoMateriaId).filter(Boolean));
    return cursoMateriaOptions
      .filter((item) => {
        const id = getCursoMateriaId(item);
        return id && !assignedIds.has(id);
      })
      .map((item) => {
        const materia = getMateriaNombre(item);
        const curso = getCursoNombre(item);
        const codigo = getMateriaCodigo(item);
        const area = getAreaNombre(item);
        const ciclo = getCicloLectivo(item) || currentCicloLectivo;
        return {
          value: getCursoMateriaId(item),
          label: `${materia} · ${curso} · ${ciclo}`,
          secondary: [codigo && `Código ${codigo}`, area].filter(Boolean).join(' · '),
          searchText: `${materia} ${curso} ${codigo} ${area} ${ciclo}`
        };
      });
  }, [currentCicloLectivo, cursoMateriaOptions, cursoMateriasAsignadas]);

  useEffect(() => {
    if (!profesorId) {
      navigate('/admin-dashboard', { replace: true });
    }
  }, [profesorId, navigate]);

  useEffect(() => {
    const fetchProfesor = async () => {
      const hasState = profesorState && Object.keys(profesorState).length > 0;
      const stateHasPermite = profesorState?.permiteCrearAsistente !== undefined
        || profesorState?.PermiteCrearAsistente !== undefined
        || profesorState?.PertmiteCrearAsistente !== undefined
        || profesorState?.permiteCrearAula !== undefined || profesorState?.PermiteCrearAula !== undefined;
      if (hasState && stateHasPermite) return;
      if (!profesorId) return;
      try {
        const prof = await usuarioService.getProfesorById(profesorId);
        if (prof) {
          setForm({
            email: prof.email || prof.Email || '',
            nombre: prof.nombre || prof.Nombre || '',
            apellido: prof.apellido || prof.Apellido || '',
            telefono: prof.telefono || prof.Telefono || '',
            dni: prof.dni || prof.Dni || '',
            legajo: prof.legajo || prof.Legajo || '',
            permiteCrearAsistente: prof.permiteCrearAsistente ?? prof.PermiteCrearAsistente ?? prof.PertmiteCrearAsistente ?? false,
            permiteCrearAula: prof.permiteCrearAula || prof.PermiteCrearAula || false,
          });
        }
      } catch (error) {
        setMessage(error.message || 'No pudimos cargar el profesor.');
        setMessageType('error');
      }
    };
    fetchProfesor();
  }, [profesorId, profesorState]);

  useEffect(() => {
    const fetchCursoMaterias = async () => {
      if (!activeColegioId || !profesorId) return;
      setCursoMateriasLoading(true);
      setCursoMateriasError('');
      try {
        const response = await usuarioService.getProfesorCursoMaterias(profesorId, activeColegioId);
        setCursoMateriaOptions(normalizeList(response.opciones || response.Opciones));
        setCursoMateriasAsignadas(normalizeList(response.asignadas || response.Asignadas));
      } catch (error) {
        setCursoMateriaOptions([]);
        setCursoMateriasAsignadas([]);
        setCursoMateriasError(error.message || 'No pudimos cargar las materias por curso.');
      } finally {
        setCursoMateriasLoading(false);
      }
    };

    fetchCursoMaterias();
  }, [activeColegioId, profesorId]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddCursoMateria = async () => {
    if (!selectedCursoMateriaId || !profesorId || !activeColegioId) return;
    const cursoMateria = cursoMateriaOptions.find((item) => getCursoMateriaId(item) === selectedCursoMateriaId);
    if (!cursoMateria) return;
    if (cursoMateriasAsignadas.some((item) => getCursoMateriaId(item) === selectedCursoMateriaId)) return;

    setIsAssociatingCursoMateria(true);
    setCursoMateriasMessage('');
    try {
      await usuarioService.assignProfesorCursoMaterias(profesorId, activeColegioId, [selectedCursoMateriaId]);
      setCursoMateriasAsignadas((prev) => [...prev, { ...cursoMateria, asignada: true, Asignada: true }]);
      setCursoMateriaOptions((prev) => prev.map((item) => (
        getCursoMateriaId(item) === selectedCursoMateriaId
          ? { ...item, asignada: true, Asignada: true }
          : item
      )));
      setSelectedCursoMateriaId('');
      setCursoMateriasMessage('Materia asociada al curso correctamente.');
      setCursoMateriasMessageType('success');
    } catch (error) {
      setCursoMateriasMessage(error.message || 'No pudimos asociar la materia al curso.');
      setCursoMateriasMessageType('error');
    } finally {
      setIsAssociatingCursoMateria(false);
    }
  };

  const handleRemoveCursoMateria = async (idCursoMateria) => {
    if (!idCursoMateria || !profesorId || !activeColegioId) return;
    setIsAssociatingCursoMateria(true);
    setCursoMateriasMessage('');
    try {
      await usuarioService.removeProfesorCursoMaterias(profesorId, activeColegioId, [idCursoMateria]);
      setCursoMateriasAsignadas((prev) => prev.filter((item) => getCursoMateriaId(item) !== idCursoMateria));
      setCursoMateriaOptions((prev) => prev.map((item) => (
        getCursoMateriaId(item) === idCursoMateria
          ? { ...item, asignada: false, Asignada: false }
          : item
      )));
      setCursoMateriasMessage('Materia desasociada del curso correctamente.');
      setCursoMateriasMessageType('success');
    } catch (error) {
      setCursoMateriasMessage(error.message || 'No pudimos desasociar la materia del curso.');
      setCursoMateriasMessageType('error');
    } finally {
      setIsAssociatingCursoMateria(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!activeColegioId) {
      setMessage('Selecciona un colegio antes de editar el profesor.');
      setMessageType('error');
      return;
    }
    if (!form.email) {
      setMessage('Completa email para continuar.');
      setMessageType('error');
      return;
    }
    const dniValue = (form.dni || '').trim();
    if (!dniValue) {
      setMessage('Completa DNI para continuar.');
      setMessageType('error');
      return;
    }

    setIsSubmitting(true);
    setMessage('Guardando cambios...');
    setMessageType('info');
    try {
      const displayName = `${form.nombre || ''} ${form.apellido || ''}`.trim() || form.email;
      const payload = {
        ...form,
        displayName,
        idColegio: activeColegioId,
        dni: dniValue,
        legajo: (form.legajo || '').trim() || null
      };
      if (!hasModuloAlumnos) {
        delete payload.permiteCrearAula;
      }
      await usuarioService.updateProfesor(profesorId, payload);
      setMessage('Profesor actualizado correctamente.');
      setMessageType('success');
      setTimeout(() => (returnTo
        ? navigate(returnTo.path, { replace: true, state: returnTo.state })
        : navigate('/admin-dashboard', { replace: true, state: { tab: 'profesores' } })), 1000);
    } catch (error) {
      setMessage(error.message || 'No pudimos actualizar el profesor.');
      setMessageType('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="new-dashboard admin-mode">
      <aside className="dashboard-sidebar">
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <i className="fas fa-university"></i>
            <span>Admin</span>
          </div>
        </div>
        <AdminMenu
          activeId="profesores"
          user={user}
          featureFlags={ColegioService.getFeatureFlags()}
          baseItems={ADMIN_FORM_MENU_ITEMS}
          onNavigate={(item) => navigateAdminMenuItem(navigate, item)}
        />
        <div className="sidebar-footer">
          <ActiveColegioBadge />
          <div className="sidebar-user">
            <div className="user-avatar">
              <i className="fas fa-user-shield"></i>
            </div>
            <div className="user-info">
              <span className="user-name">{user?.displayName || user?.name || user?.email || 'Administrador'}</span>
              <span className="user-role">Admin</span>
            </div>
          </div>
        </div>
      </aside>

      <main className="dashboard-main">
        <header className="dashboard-header">
          <div className="header-left">
            <h1 className="page-title">Editar profesor</h1>
            <p className="page-subtitle">Actualiza los datos del docente asociado al colegio {activeColegioName}.</p>
          </div>
          <div className="header-right">
            <ActiveColegioBadge />
            <UserSessionMenu user={user} onLogout={logout} showStandaloneLogout={false} userFallbackLabel="Administrador" />
          </div>
        </header>

        <div className="dashboard-content" style={{ padding: '2rem', maxWidth: '1200px' }}>
          <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <div className="content-card" style={{ flex: '1 1 520px', minWidth: '320px' }}>
            <div className="card-header">
              <div className="card-title">
                <i className="fas fa-user-edit" />
                <span>Datos del profesor</span>
              </div>
            </div>
            <div className="card-content">
              <p style={{ marginTop: 0, color: '#475569' }}>
                Edita los datos del docente. El DNI es obligatorio y se usa como contraseña inicial; el legajo es opcional.
              </p>
              <form onSubmit={handleSubmit} className="prof-create-form">
                <div className="form-field">
                  <label>Email</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    placeholder="profesor@ejemplo.com"
                  />
                </div>
                <div className="form-grid">
                  <div className="form-field">
                    <label>Nombre</label>
                    <input
                      type="text"
                      value={form.nombre}
                      onChange={(e) => handleChange('nombre', e.target.value)}
                      placeholder="Juan"
                    />
                  </div>
                  <div className="form-field">
                    <label>Apellido</label>
                    <input
                      type="text"
                      value={form.apellido}
                      onChange={(e) => handleChange('apellido', e.target.value)}
                      placeholder="Perez"
                    />
                  </div>
                </div>
                <div className="form-grid">
                  <div className="form-field">
                    <label>Teléfono</label>
                    <input
                      type="text"
                      value={form.telefono}
                      onChange={(e) => handleChange('telefono', e.target.value)}
                      placeholder="123456"
                    />
                  </div>
                  <div className="form-field">
                    <label>DNI</label>
                    <input
                      type="text"
                      value={form.dni}
                      onChange={(e) => handleChange('dni', e.target.value)}
                      placeholder="30123456"
                    />
                  </div>
                </div>
                <div className="form-field">
                  <label>Legajo (opcional)</label>
                  <input
                    type="text"
                    value={form.legajo}
                    onChange={(e) => handleChange('legajo', e.target.value)}
                    placeholder="A123"
                  />
                </div>
                <div className="form-field" style={{ padding: '0.75rem 0', borderTop: '1px solid var(--gray-200)', marginTop: '0.25rem' }}>
                  <label htmlFor="permiteCrearAsistente" style={{ display: 'block', marginBottom: '0.35rem', fontWeight: 600 }}>
                    Permisos adicionales
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: hasModuloAlumnos ? '0.5rem' : 0 }}>
                    <input
                      type="checkbox"
                      id="permiteCrearAsistente"
                      checked={!!form.permiteCrearAsistente}
                      onChange={(e) => handleChange('permiteCrearAsistente', e.target.checked)}
                      style={{ width: '18px', height: '18px' }}
                    />
                    <label htmlFor="permiteCrearAsistente" style={{ margin: 0 }}>
                      Permite crear asistentes
                    </label>
                    <span style={{ color: '#6b7280', fontSize: '0.9rem' }}>
                      Habilita al docente a crear y probar asistentes IA.
                    </span>
                  </div>
                  {hasModuloAlumnos && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                      <input
                        type="checkbox"
                        id="permiteCrearAula"
                        checked={!!form.permiteCrearAula}
                        onChange={(e) => handleChange('permiteCrearAula', e.target.checked)}
                        style={{ width: '18px', height: '18px' }}
                      />
                      <label htmlFor="permiteCrearAula" style={{ margin: 0 }}>
                        Permite crear aulas
                      </label>
                      <span style={{ color: '#6b7280', fontSize: '0.9rem' }}>
                        Habilita al docente a crear aulas del módulo alumnos.
                      </span>
                    </div>
                  )}
                </div>

                {message && (
                  <div
                    className="modal-message"
                    style={{
                      marginTop: '0.5rem',
                      borderColor: messageType === 'success' ? '#bbf7d0' : messageType === 'error' ? '#fecaca' : 'var(--gray-200)',
                      background: messageType === 'success' ? '#dcfce7' : messageType === 'error' ? '#fee2e2' : '#f8fafc',
                      color: messageType === 'error' ? '#7f1d1d' : messageType === 'success' ? '#0f5132' : 'var(--gray-800)'
                    }}
                  >
                    {isSubmitting && <i className="fas fa-spinner fa-spin" style={{ marginRight: '0.35rem' }} />}
                    {message}
                  </div>
                )}

                <div className="form-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => (returnTo ? navigate(returnTo.path, { state: returnTo.state }) : navigate('/admin-dashboard'))}>
                    {returnTo ? 'Volver' : 'Cancelar'}
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                    {isSubmitting ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-save" />}
                    <span>Guardar cambios</span>
                  </button>
                </div>
              </form>
            </div>
          </div>

            <div className="content-card professor-course-subject-card" style={{ flex: '1 1 360px', minWidth: '300px' }}>
            <div className="card-header">
              <div className="card-title">
                <i className="fas fa-book" />
                <span>Materias por curso</span>
              </div>
            </div>
            <div className="card-content">
              <p style={{ marginTop: 0, color: '#475569' }}>
                Ciclo lectivo {currentCicloLectivo}: asocia al docente a una materia dentro de un curso puntual. Esta asignación no habilita la materia en forma general.
              </p>

              {cursoMateriasLoading && <p>Cargando materias por curso...</p>}
              {cursoMateriasError && <p style={{ color: '#b91c1c' }}>{cursoMateriasError}</p>}

              {!cursoMateriasLoading && cursoMateriaOptions.length > 0 && (
                <div className="form-field">
                  <label>Materia y curso</label>
                  <div className="professor-course-subject-picker">
                    <SearchableSelect
                      value={selectedCursoMateriaId}
                      onChange={setSelectedCursoMateriaId}
                      options={cursoMateriaSelectOptions}
                      placeholder="Buscar por materia, curso, año, división o ciclo"
                      emptyText={`No hay materias por curso disponibles para ${currentCicloLectivo}.`}
                      noResultsText="No encontramos una materia por curso con esa búsqueda."
                      disabled={isAssociatingCursoMateria}
                    />
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleAddCursoMateria}
                      disabled={!selectedCursoMateriaId || isAssociatingCursoMateria}
                    >
                      {isAssociatingCursoMateria ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-plus" />}
                      <span>Agregar</span>
                    </button>
                  </div>
                </div>
              )}

              {!cursoMateriasLoading && cursoMateriaOptions.length === 0 && !cursoMateriasError && (
                <p style={{ color: '#6b7280', margin: 0 }}>
                  No hay materias asociadas a cursos para el ciclo lectivo {currentCicloLectivo}.
                </p>
              )}

              <div className="professor-course-subject-list">
                <p style={{ margin: '0 0 0.5rem 0', color: '#475569', fontWeight: 600 }}>
                  Asignaciones puntuales
                </p>
                {cursoMateriasAsignadas.length === 0 && (
                  <p style={{ color: '#6b7280', margin: 0 }}>Aún no hay materias por curso asignadas.</p>
                )}
                {cursoMateriasAsignadas.map((item) => {
                  const idCursoMateria = getCursoMateriaId(item);
                  const codigo = getMateriaCodigo(item);
                  const area = getAreaNombre(item);
                  const ciclo = getCicloLectivo(item) || currentCicloLectivo;
                  return (
                    <div className="professor-course-subject-item" key={idCursoMateria}>
                      <div>
                        <strong>{getMateriaNombre(item)}</strong>
                        <span>{getCursoNombre(item)} · {ciclo}</span>
                        <small>
                          {[codigo && `Código ${codigo}`, area].filter(Boolean).join(' · ')}
                        </small>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveCursoMateria(idCursoMateria)}
                        disabled={isAssociatingCursoMateria}
                        aria-label="Quitar asignación"
                      >
                        <i className="fas fa-times" />
                      </button>
                    </div>
                  );
                })}
              </div>

              {cursoMateriasMessage && (
                <div
                  className="modal-message"
                  style={{
                    marginTop: '0.75rem',
                    borderColor: cursoMateriasMessageType === 'success' ? '#bbf7d0' : cursoMateriasMessageType === 'error' ? '#fecaca' : 'var(--gray-200)',
                    background: cursoMateriasMessageType === 'success' ? '#dcfce7' : cursoMateriasMessageType === 'error' ? '#fee2e2' : '#f8fafc',
                    color: cursoMateriasMessageType === 'error' ? '#7f1d1d' : cursoMateriasMessageType === 'success' ? '#0f5132' : 'var(--gray-800)'
                  }}
                >
                  {isAssociatingCursoMateria && <i className="fas fa-spinner fa-spin" style={{ marginRight: '0.35rem' }} />}
                  {cursoMateriasMessage}
                </div>
              )}
            </div>
          </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminEditProfesor;
